import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Skeleton } from '@/shared-ui/Skeleton';
import { Logo } from '@/shared-ui/Logo';
import { NoteCard } from './components/NoteCard';
import { QuickText } from './components/QuickText';
import { PendingTasksStrip } from './components/PendingTasksStrip';
import { RecordingSheet } from '@/features/recording/RecordingSheet';
import { notesApi } from '@/core/api/endpoints/notes';
import { useNoteStore } from '@/core/stores/noteStore';
import { useAuthStore } from '@/core/stores/authStore';
import { useInfiniteScroll } from '@/utils/useInfiniteScroll';
import { usePullToRefresh } from '@/utils/usePullToRefresh';
import { onAnyTaskTerminal } from '@/core/services/taskDriver';
import { CATEGORY_LABEL, NOTE_CATEGORIES, type NoteCategory } from '@/core/models/note';
import { cn } from '@/utils/cn';
import { ApiError } from '@/core/api/types';

export function HomeView() {
  const navigate = useNavigate();
  const {
    ids,
    byId,
    total,
    page,
    pageSize,
    category,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    setCategory,
    setList,
    appendList,
    upsert,
    setLoading,
    setLoadingMore,
    setRefreshing,
    nextFetchToken,
    isCurrentToken,
  } = useNoteStore();
  const user = useAuthStore((s) => s.user);
  const [recOpen, setRecOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ---- list fetching ----------------------------------------------------

  /** Single source of truth for fetching the first page. mode picks UX. */
  const fetchFirstPage = useCallback(
    async (cat: NoteCategory | 'all', mode: 'initial' | 'refresh') => {
      const token = nextFetchToken();
      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);
      setErr(null);
      try {
        const res = await notesApi.list({
          page: 1,
          page_size: pageSize,
          category: cat === 'all' ? undefined : cat,
        });
        // drop result if a newer fetch superseded us (e.g. category change
        // happened mid-flight). prevents the list jumping to stale data.
        if (!isCurrentToken(token)) return;
        setList(res.items, res.total, res.page, res.page_size);
      } catch (e) {
        if (!isCurrentToken(token)) return;
        setErr(e instanceof ApiError ? e.message : '加载失败');
      } finally {
        if (mode === 'initial') setLoading(false);
        else setRefreshing(false);
      }
    },
    [pageSize, setList, setLoading, setRefreshing, nextFetchToken, isCurrentToken]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const res = await notesApi.list({
        page: next,
        page_size: pageSize,
        category: category === 'all' ? undefined : category,
      });
      appendList(res.items, res.page);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '加载更多失败');
    } finally {
      setLoadingMore(false);
    }
  }, [appendList, category, hasMore, loadingMore, page, pageSize, setLoadingMore]);

  // initial load + category-switch reload (no list flash — old list stays
  // visible until new results arrive, see noteStore.setCategory).
  useEffect(() => {
    void fetchFirstPage(category, ids.length === 0 ? 'initial' : 'refresh');
    // intentional: ids.length is a "first time?" hint, not a dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // when any task terminates, refresh so the resulting note appears at the
  // top of the list — happens regardless of which page user is on.
  useEffect(() => {
    return onAnyTaskTerminal((s) => {
      if (s.status === 'completed' && s.note_id) {
        // best-effort fetch the single note; if it fails, fall back to a
        // full refresh so the user isn't left with a stale list.
        notesApi
          .get(s.note_id)
          .then((note) => upsert(note))
          .catch(() => {
            void fetchFirstPage(category, 'refresh');
          });
      }
    });
  }, [category, fetchFirstPage, upsert]);

  // ---- pull-to-refresh --------------------------------------------------

  // window is the actual scroller — pass null so the hook listens on it
  const ptr = usePullToRefresh(null, {
    onRefresh: () => fetchFirstPage(category, 'refresh'),
    disabled: refreshing,
  });

  // ---- infinite scroll --------------------------------------------------

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore,
    loading: loading || loadingMore || refreshing,
    onLoadMore: () => void loadMore(),
  });

  const notes = ids.map((id) => byId[id]).filter(Boolean);
  const greeting = pickGreeting();

  return (
    <div className="relative min-h-screen pb-32">
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={20} />
          </div>
          <div className="text-xs text-fg-subtle flex items-center gap-3">
            <button
              onClick={() => void fetchFirstPage(category, 'refresh')}
              disabled={refreshing || loading}
              aria-label={refreshing ? '刷新中' : '刷新'}
              aria-busy={refreshing}
              className={cn(
                'rounded-full border border-border px-2 py-0.5 text-[11px] transition-colors',
                refreshing ? 'opacity-60' : 'hover:border-accent/40 hover:text-fg'
              )}
            >
              {refreshing ? '刷新中…' : '刷新'}
            </button>
            <span>
              {greeting}
              {user?.nickname ? `，${user.nickname}` : ''}
            </span>
          </div>
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-3 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(['all', ...NOTE_CATEGORIES] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={cn(
                'shrink-0 h-8 rounded-full px-3 text-xs font-medium border transition-all',
                category === c
                  ? 'bg-gradient-to-r from-accent to-accent-2 text-accent-contrast border-transparent glow-accent'
                  : 'border-border text-fg-muted hover:text-fg'
              )}
            >
              {c === 'all' ? '全部' : CATEGORY_LABEL[c]}
            </button>
          ))}
        </div>
      </header>

      <PullIndicator distance={ptr.distance} armed={ptr.armed} refreshing={ptr.refreshing} />

      <main
        className="mx-auto max-w-3xl px-4 py-4 space-y-4"
        style={{
          transform:
            ptr.distance > 0
              ? `translate3d(0, ${Math.round(ptr.distance)}px, 0)`
              : undefined,
          transition: ptr.refreshing
            ? 'transform 0.2s ease-out'
            : ptr.distance > 0
              ? 'none'
              : 'transform 0.25s ease-out',
        }}
      >
        <QuickText />

        <PendingTasksStrip />

        {err && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 p-3 text-sm text-danger flex items-center justify-between gap-3">
            <span>{err}</span>
            <button
              onClick={() => void fetchFirstPage(category, 'refresh')}
              className="text-xs underline"
            >
              重试
            </button>
          </div>
        )}

        {loading && notes.length === 0 && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        )}

        {!loading && notes.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-fg-muted">
            还没有笔记。按下右下角按钮开始录音，或在上面输入一段文字。
          </div>
        )}

        <div className="space-y-3">
          {notes.map((n, i) => (
            <NoteCard key={n.id} note={n} index={i} />
          ))}
        </div>

        {hasMore && (
          <div ref={sentinelRef} className="py-4 text-center text-xs text-fg-subtle">
            {loadingMore ? '加载中…' : '滑动以加载更多'}
          </div>
        )}

        {!hasMore && notes.length > 0 && (
          <div className="pt-2 text-center text-xs text-fg-subtle">已全部加载 · 共 {total} 条</div>
        )}
      </main>

      <motion.button
        onClick={() => setRecOpen(true)}
        whileTap={{ scale: 0.93 }}
        whileHover={{ scale: 1.04 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 22, stiffness: 260, delay: 0.1 }}
        className="fixed bottom-20 right-6 h-16 w-16 rounded-full flex items-center justify-center text-2xl bg-gradient-to-br from-accent to-accent-2 text-accent-contrast glow-accent"
      >
        <motion.span
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          🎙
        </motion.span>
      </motion.button>

      <RecordingSheet
        open={recOpen}
        onClose={() => setRecOpen(false)}
        onTaskCreated={(taskId) => navigate(`/tasks/${taskId}`)}
      />
    </div>
  );
}

function PullIndicator({
  distance,
  armed,
  refreshing,
}: {
  distance: number;
  armed: boolean;
  refreshing: boolean;
}) {
  if (distance === 0 && !refreshing) return null;
  const opacity = Math.min(1, distance / 70);
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-[68px] z-20 flex justify-center"
      style={{ opacity: refreshing ? 1 : opacity }}
    >
      <div
        className={cn(
          'rounded-full border border-border bg-bg-elevated/80 px-3 py-1 text-[11px] backdrop-blur',
          armed || refreshing ? 'text-accent border-accent/40' : 'text-fg-muted'
        )}
      >
        {refreshing ? '刷新中…' : armed ? '松开以刷新' : '下拉刷新'}
      </div>
    </div>
  );
}

function pickGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return '夜深了';
  if (h < 11) return '早上好';
  if (h < 13) return '中午好';
  if (h < 18) return '下午好';
  if (h < 22) return '晚上好';
  return '深夜好';
}
